// One templated page: controller → view, with the template's error and
// loading states (ARCHITECTURE §5.2).
//
//   [Frame page=K]                         (only when the template has one)
//     <PageErrorBoundary fallback=PageError>
//       [<Suspense fallback=RouteLoading>]  (only with a Frame, see below)
//         <Page/>   = the controller hook + <View vm/>
//
// Parity rules for classic (audit R2, R10):
// - The page component sits where the old page component sat, so its
//   descendants' effects (Navigation's nav-menu fetch, …) still run before
//   the controller's effects, and lazy pages mount their controller only
//   after the chunk loads (the lazy factory resolves to the bound page).
// - No new Suspense boundary for templates without a Frame (classic): a lazy
//   page suspends the app-level boundary in App.tsx, exactly as today, so the
//   full-screen PageLoader replaces the whole tree (Fan AI bubble included)
//   and AppRoutes' own effects (page tracking, Fan AI config) still wait for
//   the chunk. Templates with a Frame get a page-level boundary so their
//   chrome stays put while a page chunk loads.
// - One stable component per (template, page key), so /schedule ↔
//   /schedule/:id and /game/1 → /game/2 keep the page mounted, as today.

import { Component, lazy, Suspense, useState, type ComponentType, type ErrorInfo, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useSiteTemplate } from './hostContext'
import { EntitySportGate } from './EntitySportGate'
import { eagerController, loadController, type PageController } from '../pages/controllers'
import type { PageView, SitePageKey, SiteTemplate } from './types'
import { usePageTitle } from './pageTitle'

function bind<K extends SitePageKey>(useController: PageController<K>, View: PageView<K>, page: K): ComponentType {
  function Page() {
    const vm = useController()
    usePageTitle(page, vm)
    return <View vm={vm} />
  }
  Page.displayName = `Page(${page})`
  return Page
}

const pageCache = new WeakMap<SiteTemplate, Map<SitePageKey, ComponentType>>()

/** The stable page component for a template + page key. */
function getPageComponent<K extends SitePageKey>(template: SiteTemplate, page: K): ComponentType {
  let byPage = pageCache.get(template)
  if (!byPage) pageCache.set(template, (byPage = new Map()))
  const cache = byPage
  const hit = cache.get(page)
  if (hit) return hit
  const entry = template.pages[page] as { eager: PageView<K> } | { load: () => Promise<{ default: PageView<K> }> }
  const ctrl = eagerController(page)
  let made: ComponentType
  if ('eager' in entry && ctrl) {
    made = bind(ctrl, entry.eager, page)
  } else {
    const loadView = 'eager' in entry ? () => Promise.resolve({ default: entry.eager }) : entry.load
    // React.lazy keeps a rejected import forever, so a failed chunk load (a
    // network blip, a stale chunk after a deploy) re-threw on every "Try
    // again" and every return to the page (#1279). Drop the entry instead:
    // the next PageRoute render makes a fresh lazy. A stale tab is normally
    // reloaded first (installStaleChunkReload in main.tsx).
    made = lazy(async () => {
      try {
        const [view, controller] = await Promise.all([loadView(), loadController(page)])
        return { default: bind(controller, view.default, page) }
      } catch (error) {
        if (cache.get(page) === made) cache.delete(page)
        throw error
      }
    })
  }
  cache.set(page, made)
  return made
}

interface BoundaryProps { fallback: ComponentType<{ error: Error; reset: () => void }>; resetKey: string; onReset?: () => void; children: ReactNode }
interface BoundaryState { error: Error | null; key: string }

/** Catches a page that throws (today: a blank screen) and shows the template's PageError. Resets on navigation. */
export class PageErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null, key: this.props.resetKey }

  static getDerivedStateFromError(error: Error): Partial<BoundaryState> {
    return { error }
  }

  static getDerivedStateFromProps(props: BoundaryProps, state: BoundaryState): Partial<BoundaryState> | null {
    if (props.resetKey !== state.key) return { key: props.resetKey, error: null }
    return null
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[site-templates] page crashed', error, info.componentStack)
  }

  render() {
    const { error } = this.state
    if (error) {
      const Fallback = this.props.fallback
      return <Fallback error={error} reset={() => { this.setState({ error: null }); this.props.onReset?.() }} />
    }
    return this.props.children
  }
}

/**
 * The private site's sign-in wall (registration step 33), rendered by
 * SiteTemplateHost in place of a gated route while a members-only league's
 * visitor is not admitted. No Frame: a Frame's chrome fetches gated data
 * (nav menu, teams, the scores strip), so each template's view draws its own
 * root. No EntitySportGate: the wall has no entity, and it must not use up
 * the session's deep-link slot (`initialPath`), so the real page still
 * resolves its sport once admitted. Its own Suspense (the template's
 * RouteLoading) for the lazy view chunk.
 */
export function MembersOnlyRoute() {
  const { template } = useSiteTemplate()
  const Page = getPageComponent(template, 'membersOnly')
  const { RouteLoading, PageError } = template.states
  return (
    <PageErrorBoundary fallback={PageError} resetKey="membersOnly">
      <Suspense fallback={<RouteLoading />}>
        <Page />
      </Suspense>
    </PageErrorBoundary>
  )
}

export function PageRoute({ page }: { page: SitePageKey }) {
  const { template } = useSiteTemplate()
  const { pathname } = useLocation()
  // "Try again" re-renders this route, so a page whose chunk failed gets a fresh import.
  const [, setAttempt] = useState(0)
  const Page = getPageComponent(template, page)
  const { Frame } = template.shell
  const { RouteLoading, PageError } = template.states
  // #416: a deep link resolves its entity's sport before the page mounts.
  const gated = <EntitySportGate page={page} pathname={pathname}><Page /></EntitySportGate>
  const inner = Frame ? <Suspense fallback={<RouteLoading />}>{gated}</Suspense> : gated
  const body = <PageErrorBoundary fallback={PageError} resetKey={pathname} onReset={() => setAttempt((n) => n + 1)}>{inner}</PageErrorBoundary>
  return Frame ? <Frame page={page}>{body}</Frame> : body
}
